/*
 * Revisit route — /plan-builder/results/:submissionId
 *
 * Fetches the public (PII-stripped) submission from the API, recomputes
 * the score + actions from the stored diagnostic answers, and renders
 * ResultsPage in fetched mode. The capture card is hidden (the original
 * buyer already submitted; a visitor with the link shouldn't see a form
 * to email themselves a plan they didn't fill).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@shared/api/client';
import { createEmptyLens, type SharedLens } from '@shared/lens';
import { ResultsPage } from './components/ResultsPage';
import { PlanBuilderConfigContext } from './PlanBuilderConfigContext';
import { usePlanBuilderConfig } from './usePlanBuilderConfig';
import { clearLocalState } from './state';
import { clearSubmissionId } from './submission';
import type { PlanBuilderSubmissionPayload } from './types';
import './PlanBuilder.css';

/** Merge the stored payload lens subset back into a full SharedLens shape. */
function hydrateStoredLens(stored: PlanBuilderSubmissionPayload['lens']): SharedLens {
  return {
    ...createEmptyLens(),
    jobStatement: stored.jobStatement,
    frictionPhase: stored.frictionPhase,
    outcomes: stored.outcomes,
    flexibility: stored.flexibility,
    emotional: stored.emotional,
    redFlagWordsHit: stored.redFlagWordsHit,
  };
}

export function PlanBuilderResultsView() {
  const { submissionId = '' } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const { config } = usePlanBuilderConfig();
  const [data, setData] = useState<PlanBuilderSubmissionPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Apply admin var overrides (from config.vars) at the document root,
  // mirroring PlanBuilder.tsx so shared links render with admin branding.
  useEffect(() => {
    if (!config.vars) return;
    const root = document.documentElement;
    const previous: Array<[string, string]> = [];
    for (const [k, v] of Object.entries(config.vars)) {
      previous.push([k, root.style.getPropertyValue(k)]);
      root.style.setProperty(k, v);
    }
    return () => {
      for (const [k, v] of previous) {
        if (v) root.style.setProperty(k, v);
        else root.style.removeProperty(k);
      }
    };
  }, [config.vars]);

  useEffect(() => {
    if (!submissionId) {
      setError("No submission ID in the URL.");
      return;
    }
    let cancelled = false;
    api
      .getPublicSubmission<PlanBuilderSubmissionPayload>(submissionId)
      .then((res) => {
        if (cancelled) return;
        if (res.tool_id !== 'plan_builder') {
          setError("That link doesn't point to a plan builder report.");
          return;
        }
        setData(res.payload);
      })
      .catch(() => {
        if (cancelled) return;
        setError(
          "We couldn't load that plan. The link may be wrong, or the plan may have been removed.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  const handleRetake = () => {
    clearLocalState();
    clearSubmissionId();
    navigate('/plan-builder');
  };

  if (error) {
    return (
      <main className="page-shell">
        <div className="pb-fetch-error">
          <h2>Plan not found</h2>
          <p>{error}</p>
          <button type="button" className="pb-btn-next" onClick={handleRetake}>
            Start a new plan
          </button>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="page-shell">
        <div className="pb-fetch-loading">Loading your plan…</div>
      </main>
    );
  }

  const lens = hydrateStoredLens(data.lens);

  return (
    <PlanBuilderConfigContext.Provider value={config}>
      <div className="pb-root">
        <main>
          <ResultsPage
            lens={lens}
            answers={data.diagnostic}
            submissionId={submissionId}
            mode="fetched"
            onRetake={handleRetake}
          />
        </main>
      </div>
    </PlanBuilderConfigContext.Provider>
  );
}
